import axios from 'axios';

// Create axios instance
// Use full URL to Laragon PHP backend
const getBaseURL = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  // Backend is at src/backend/api, Laragon serves it at /real_estate_react/src/backend/api
  // Backend is at src/backend/api, Laragon serves it at /real_estate_react/src/backend/api
  // Use current hostname (localhost or network IP) to connect directly to Laragon
  // The backend handles CORS (Access-Control-Allow-Origin: *), so this works fine
  const hostname = window.location.hostname;
  return `http://${hostname}/real_estate_react/src/backend/api`;
};

const api = axios.create({
  baseURL: getBaseURL(),
  headers: {
    'Content-Type': 'application/json',
  },
});

// Recover JSON when PHP warnings/notices are printed before the payload
const normalizeResponsePayload = (data) => {
  if (typeof data !== 'string') {
    return data;
  }

  const jsonStart = data.indexOf('{');
  if (jsonStart === -1) {
    return data;
  }

  try {
    return JSON.parse(data.slice(jsonStart));
  } catch {
    return data;
  }
};

// Request interceptor - Add JWT token and company ID
api.interceptors.request.use(
  (config) => {
    // Rewrite path-info style URLs to query parameter style for Hostinger compatibility
    // e.g., /auth.php/login -> /auth.php?route=login
    // e.g., /employees.php/123/financials -> /employees.php?route=123/financials
    if (config.url) {
      const phpPathMatch = config.url.match(/^(\/[^?]*\.php)\/(.+)$/);
      if (phpPathMatch) {
        config.url = phpPathMatch[1];
        config.params = { ...config.params, route: phpPathMatch[2] };
      }
    }

    // Debug: Log the request URL
    console.log('API Request:', config.method?.toUpperCase(), config.baseURL + config.url);

    const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    const companyId = localStorage.getItem('company_id');
    if (companyId) {
      config.headers['X-Company-ID'] = companyId;
    }

    const branchId = localStorage.getItem('branch_id');
    if (branchId && branchId !== 'all') {
      config.headers['X-Branch-ID'] = branchId;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Handle errors
api.interceptors.response.use(
  (response) => {
    const payload = normalizeResponsePayload(response.data);

    // Handle different response formats
    if (payload && payload.success !== undefined) {
      if (payload.success) {
        // Use `in` check so empty arrays/null are returned correctly (|| would fall through)
        if (Object.prototype.hasOwnProperty.call(payload, 'data')) {
          return payload.data;
        }
        return payload;
      } else {
        return Promise.reject(new Error(payload.error || payload.message || 'Request failed'));
      }
    }
    return payload || response;
  },
  (error) => {
    // Debug: Log the error
    console.error('API Error:', error.message, error.response?.status, error.config?.url);

    // Handle network errors
    if (!error.response) {
      return Promise.reject(new Error('Network error. Please check your connection.'));
    }

    // Handle 401 Unauthorized
    if (error.response.status === 401) {
      // Check if this is a login request - if so, don't treat as session expiration
      const isLoginRequest = error.config?.url?.includes('/auth.php/login') ||
        error.config?.url?.includes('/auth.php/register');

      if (isLoginRequest) {
        // For login/register, return the actual error message from backend
        const errorMessage = error.response.data?.error ||
          error.response.data?.message ||
          'Invalid credentials';
        return Promise.reject(new Error(errorMessage));
      }

      // For other requests, treat as session expiration
      const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
      if (token) {
        // Only clear session if we had a token (authenticated request)
        localStorage.removeItem('auth_token');
        sessionStorage.removeItem('auth_token');
        localStorage.removeItem('company_id');
        window.location.href = '/login';
        return Promise.reject(new Error('Session expired. Please login again.'));
      }
    }

    // Handle other errors
    const errorMessage = error.response.data?.error ||
      error.response.data?.message ||
      error.message ||
      'An error occurred';
    return Promise.reject(new Error(errorMessage));
  }
);

// Export named wrapper functions
export const get = (url, config = {}) => api.get(url, config);
export const post = (url, data, config = {}) => api.post(url, data, config);
export const put = (url, data, config = {}) => api.put(url, data, config);
export const del = (url, config = {}) => api.delete(url, config);

export default api;

